<template>
  <v-row>
    <v-col>
      <info-card :title="campanha.name" :description="campanha.description" :status="campanha.status" :with-margins="false">
        <v-row>
          <v-col cols="4">
            <h3 class="info-header">Período de validade:</h3>
            <p class="info-description">
              {{ DateTime.fromFormat(campanha.start_date.toString(), 'yyyy-MM-dd').toFormat('dd/MM/yyyy') }} -
              {{ DateTime.fromFormat(campanha.end_date.toString(), 'yyyy-MM-dd').toFormat('dd/MM/yyyy') }}
            </p>
          </v-col>

          <v-col v-if="!campanha.industry">
            <h3 class="info-header">Modelo da campanha:</h3>
            <p class="info-description">{{ campanha.activatable ? 'Ativável' : 'Livre' }}</p>
          </v-col>

          <v-col v-if="campanha.industry">
            <h3 class="info-header">Indústria:</h3>
            <p class="info-description">{{ campanha.industry.name }}</p>
          </v-col>

          <v-col v-if="campanha.industry">
            <h3 class="info-header">Formato:</h3>
            <p class="info-description">{{ getOfferType }}</p>
          </v-col>

          <v-col v-if="campanha.industry && isDigitalVoucherCampaign">
            <h3 class="info-header">Tipo do voucher:</h3>
            <p class="info-description">{{ getVoucherType(campanha.dynamic_rule_offer_sales[0].benefits.type) }}</p>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="4">
            <h3 class="info-header">Público-alvo:</h3>
            <div v-if="campanha.segments && campanha.segments.length">
              <v-alert v-if="campaignCreatedBeforeViewSegmentation" class="alert-color ma-0" dense icon="mdi-alert-outline">
                A visualização dos critérios de segmentação está disponível apenas para campanhas criadas após {{ viewSegmentationDate }}.
              </v-alert>

              <v-alert v-if="!campaignCreatedBeforeViewSegmentation && campanha.has_segment_data" class="ma-0" color="blue" dense text type="info">
                Clique em cima do segmento e uma nova aba será aberta para visualizar os critérios da segmentação.
              </v-alert>

              <v-chip-group :column="showAllSegments" show-arrows active-class="primary--text">
                <v-chip v-for="(segment, index) in campanha.segments" :key="index" small :ripple="false" @click="goToSegmentation(index)">
                  <span v-if="segment"
                    ><b>{{ segment.name }}</b
                    >{{ segment.clients_qty_at_campaign !== null ? ` (${segment.clients_qty_at_campaign} clientes)` : '' }}</span
                  >
                </v-chip>
              </v-chip-group>
              <a v-if="campanha.segments.length > 1" class="user-select-none mt-3 mb-0 body-2" @click="showAllSegments = !showAllSegments">{{
                showAllSegments ? 'Minimizar' : 'Mostrar todos'
              }}</a>
              <export-client-button :segment="campanha.segments[0]" small />
            </div>
            <div v-else>
              <p class="info-description">Sem segmentos selecionados</p>
            </div>
          </v-col>
          <v-col cols="4">
            <h3 class="info-header">Lojas de exibição:</h3>
            <div v-if="campanha.units && campanha.units.length">
              <v-chip-group :column="showAllUnits" show-arrows active-class="primary--text">
                <v-chip v-for="(unit, index) in campanha.units" :key="index" small :ripple="false">
                  <span v-if="unit"
                    ><b>{{ unit.name }}</b></span
                  >
                </v-chip>
              </v-chip-group>
              <a v-if="campanha.units.length > 1" class="user-select-none mt-3 mb-0 body-2" @click="showAllUnits = !showAllUnits">{{
                showAllUnits ? 'Minimizar' : 'Mostrar todas'
              }}</a>
            </div>
            <div v-else>
              <p class="info-description">Sem lojas selecionados</p>
            </div>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <h3 class="info-header">Produtos:</h3>
            <div v-if="listAllProducts && listAllProducts.length">
              <v-chip-group :column="showAllProducts" show-arrows active-class="primary--text">
                <v-chip v-for="(product, index) in listAllProducts" :key="index" small :ripple="false">
                  <span>{{ product.sku }} - {{ product.name }}</span>
                </v-chip>
              </v-chip-group>
              <a class="user-select-none mt-3 mb-0 body-2" @click="showAllProducts = !showAllProducts">{{ showAllProducts ? 'Minimizar' : 'Mostrar todos' }}</a>
            </div>
            <div v-else>
              <p class="info-description">Sem produtos selecionados</p>
            </div>
          </v-col>
        </v-row>

        <v-row v-if="showViewCampaignButton && !isFranchisee">
          <v-col>
            <mf-button outlined label="Ver campanha" @click="redirectToEdit()" />
          </v-col>
        </v-row>
      </info-card>
    </v-col>
  </v-row>
</template>

<script>
import ExportClientButton from '@/modules/clientes/segmentation/views/components/ExportClientButton.vue'
import { DateTime } from 'luxon'
import { assertArray } from '@/config/helpers.js'
import { mergeAllIndividualAndGroupProductsByOffer, mergeAllComboProducts } from '@/modules/campanhas/gestao-ofertas-v2/helpers/products.js'
import { getProductFormatTypeLabel } from '@/modules/mobile/ofertas/helpers/productType.js'
import { getVoucherType } from '@/modules/industria/helpers/getVoucherType'

export default {
  name: 'CampaignResultsBanner',
  components: {
    ExportClientButton,
    InfoCard: () => import('@/modules/campanhas/views/components/results/InfoCard.vue'),
  },
  props: {
    campanha: {
      type: Object,
      required: true,
    },
    showViewCampaignButton: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    DateTime: DateTime,
    showAllSegments: false,
    showAllProducts: false,
    showAllUnits: false,
  }),
  computed: {
    listAllProducts() {
      const offerSalesProducts = mergeAllIndividualAndGroupProductsByOffer(this.campanha.offer_sales, 'offer_sales')
      const percentageOfferSalesProducts = mergeAllIndividualAndGroupProductsByOffer(this.campanha.percentage_offer_sales, 'percentage_offer_sales')
      const dynamicPricingPercentageOfferSalesProducts = mergeAllIndividualAndGroupProductsByOffer(
        this.campanha.dynamic_pricing_percentage_offer_sales,
        'dynamic_pricing_percentage_offer_sales'
      )
      const takeAndPayOfferSalesProducts = mergeAllIndividualAndGroupProductsByOffer(this.campanha.takepay_offer_sales, 'takepay_offer_sales')
      const cashbackPercentageOfferSalesProducts = mergeAllIndividualAndGroupProductsByOffer(
        this.campanha.cashback_percentage_offer_sales,
        'cashback_percentage_offer_sales'
      )
      const cashbackAbsoluteOfferSalesProducts = mergeAllIndividualAndGroupProductsByOffer(
        this.campanha.cashback_absolute_offer_sales,
        'cashback_absolute_offer_sales'
      )
      const pointsOfferSalesProducts = mergeAllIndividualAndGroupProductsByOffer(this.campanha.points_offer_sales, 'points_offer_sales')

      const comboOfferSalesProducts = assertArray(this.campanha.combo_offer_sales).reduce((productList, product) => {
        productList.push(...mergeAllComboProducts(product.target_products, 'target_products'))
        productList.push(...mergeAllComboProducts(product.trigger_products, 'trigger_products'))

        return productList
      }, [])

      const nextBuyCashbackOfferSalesProducts = this.campanha.next_buy_cashback_offer_sales || []

      let dynamicRuleOfferSalesProducts = []
      if (this.campanha?.dynamic_rule_offer_sales?.length > 0) {
        this.campanha.dynamic_rule_offer_sales.forEach((dynamic_rule) => {
          dynamicRuleOfferSalesProducts.push(dynamic_rule.trigger_products[0])
        })
      }

      let crossSellOfferSalesProducts = []
      if (this.campanha?.cross_sell_offer_sales?.length > 0) {
        this.campanha.cross_sell_offer_sales.forEach((cross_sell) => {
          cross_sell.target_products.forEach((target_product) => {
            crossSellOfferSalesProducts.push(target_product)
          })
          cross_sell.trigger_products.forEach((trigger_product) => {
            crossSellOfferSalesProducts.push(trigger_product)
          })
        })
      }

      return [
        ...offerSalesProducts,
        ...percentageOfferSalesProducts,
        ...dynamicPricingPercentageOfferSalesProducts,
        ...takeAndPayOfferSalesProducts,
        ...comboOfferSalesProducts,
        ...cashbackPercentageOfferSalesProducts,
        ...cashbackAbsoluteOfferSalesProducts,
        ...pointsOfferSalesProducts,
        ...nextBuyCashbackOfferSalesProducts,
        ...dynamicRuleOfferSalesProducts,
        ...crossSellOfferSalesProducts,
      ]
    },

    campaignCreatedBeforeViewSegmentation() {
      if (this.campanha.created_at) {
        return DateTime.fromISO(this.campanha.created_at).startOf('day') <= DateTime.fromISO(this.dateViewSegmentation).startOf('day')
      }

      return false
    },

    viewSegmentationDate() {
      return DateTime.fromISO(this.dateViewSegmentation).toFormat('dd/MM/yyyy')
    },

    getOfferType() {
      if (!this.campanha.types) {
        return '-'
      }

      return this.campanha.types.length > 1 ? 'Múltiplos' : getProductFormatTypeLabel(this.campanha.types[0])
    },

    isDigitalVoucherCampaign() {
      return this.campanha.types.length > 0 ? this.campanha.types[0] === 'dynamic_rule_offer_sales' : false
    },

    isFranchisee() {
      if (!this.$getLoggedUser().is_franchisee) return false
      return this.$getLoggedUser().is_franchisee
    },
  },
  watch: {},
  mounted() {},
  methods: {
    getVoucherType,

    goToSegmentation(index) {
      if (!this.campanha.has_segment_data) {
        this.$snackbar({ message: 'Não é possível visualizar critérios desta campanha', snackbarColor: 'error' })
        return
      }

      const segment = this.campanha.segments[index]

      if (!segment) return

      const routeData = this.$router.resolve({
        name: 'visualizar-segmentacao',
        params: {
          campaignId: this.campanha._id,
          segmentId: segment._id,
        },
      })
      window.open(routeData.href, '_blank')
    },

    redirectToEdit() {
      let routeName = this.campanha.industry ? 'ofertas-trade-visualizar' : 'gestao-ofertas-editar'
      this.$router.push({ name: routeName, params: { id: this.campanha._id } }).catch(() => {})
    },
  },
}
</script>

<style lang="scss" scoped>
.info-header {
  font-size: 18px;
  color: $cinza-d10;
  font-weight: bold;
}
</style>
